/**
 * Visibility rules for the one-time "AI Credits → GAUs" rename notice.
 *
 * The notice is temporary: it exists only to tell existing users the term
 * changed, and is meant to be deleted once the rename has settled. Two rules
 * gate it — a per-account age cutoff and a per-user dismissal — kept here as
 * pure helpers so they can be unit-tested without a browser.
 */

/**
 * The day the rename went live. Accounts created on or after this instant only
 * ever saw "GAUs", so the "AI Credits are now called GAUs" notice is
 * meaningless to them and must not render.
 */
export const GAU_RENAME_GO_LIVE = new Date("2026-07-22T00:00:00.000Z");

const DISMISS_STORAGE_PREFIX = "gau-rename-callout-dismissed";

/**
 * Whether the account is new enough that it never knew the "AI Credits" term.
 * Returns `true` for accounts created on or after `GAU_RENAME_GO_LIVE` (the
 * go-live instant itself counts as new). Fails open (returns `false`, i.e.
 * "show the notice") when the timestamp is missing or unparseable, preserving
 * the notice's original always-on behavior.
 */
export function isAccountNewerThanGauRename(
  createdAt: Date | string | null | undefined,
): boolean {
  if (createdAt == null) return false;
  const createdMs = new Date(createdAt).getTime();
  if (Number.isNaN(createdMs)) return false;
  return createdMs >= GAU_RENAME_GO_LIVE.getTime();
}

/**
 * localStorage key for a user's dismissal. Scoped per user so dismissing on a
 * shared browser doesn't hide the notice from the next person to sign in.
 */
export function gauRenameDismissStorageKey(scopeId?: string | null): string {
  return `${DISMISS_STORAGE_PREFIX}:${scopeId || "anonymous"}`;
}
